import React, { useState, useEffect, useRef } from 'react';
import Navigation from './Navigation';
import { ArrowLeft, MapPin, DollarSign, FileText, X, Mail, Phone, CheckCircle, AlertCircle, Loader, Camera } from 'lucide-react';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

export default function PostServiceForm() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    mainCategory: '', subcategory: '', title: '', description: '',
    location: '', priceType: 'hourly', price: '', experience: '',
    availability: [], skills: [], phone: '', email: '', responseTime: '24'
  });
  const [skillInput, setSkillInput] = useState('');
  const [selectedImages, setSelectedImages] = useState([]); // { file, preview }
  const imageInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) window.location.href = '/auth';
  }, []);

  const serviceCategories = {
    'home-construction': { label: 'Home and Construction', subcategories: ['Carpentry', 'Masonry', 'Roofing', 'Flooring', 'Tiling', 'Painting', 'Plastering'] },
    'repair-maintenance': { label: 'Repair and Maintenance', subcategories: ['Plumbing', 'Electrical', 'AC Repair', 'Appliance Repair', 'General Repairs', 'Handyman'] },
    'cleaning-gardening': { label: 'Cleaning and Gardening', subcategories: ['House Cleaning', 'Office Cleaning', 'Gardening', 'Landscaping', 'Pool Cleaning', 'Pest Control'] },
    'photography-video': { label: 'Photography and Video', subcategories: ['Portrait Photography', 'Event Photography', 'Product Photography', 'Videography', 'Drone Photography', 'Video Editing', 'Reels and Shorts'] },
    'content-social': { label: 'Content and Social Media', subcategories: ['Content Creation', 'Social Media Management', 'Instagram Growth', 'TikTok Content', 'YouTube Management', 'Copywriting', 'Blogging'] },
    'design-creative': { label: 'Design and Creative', subcategories: ['Graphic Design', 'Logo Design', 'UI/UX Design', 'Animation', 'Illustration', 'Brand Identity', 'Print Design'] },
    'music-audio': { label: 'Music and Audio', subcategories: ['Music Production', 'Singing', 'Music Lessons', 'DJ Services', 'Podcast Editing', 'Voiceover', 'Sound Design'] },
    'it-technology': { label: 'IT and Technology', subcategories: ['Web Development', 'App Development', 'Software Support', 'Network Setup', 'Data Recovery', 'Tech Consulting', 'Gaming and Streaming'] },
    'education-training': { label: 'Education and Training', subcategories: ['Tutoring', 'Language Classes', 'Coding Classes', 'Art Classes', 'Dance Classes', 'Life Coaching', 'Career Coaching'] },
    'beauty-wellness': { label: 'Beauty and Wellness', subcategories: ['Hair Styling', 'Makeup', 'Spa Services', 'Massage', 'Personal Training', 'Yoga', 'Mental Health Support', 'Nutrition Coaching'] },
    'events-entertainment': { label: 'Events and Entertainment', subcategories: ['Event Planning', 'MC Services', 'Catering', 'Decoration', 'Photography', 'DJ Services', 'Live Music'] },
    'business-professional': { label: 'Business and Professional', subcategories: ['Accounting', 'Legal Services', 'Consulting', 'Virtual Assistant', 'Translation', 'Data Entry', 'Customer Support'] },
    'transport-logistics': { label: 'Transport and Logistics', subcategories: ['Moving Services', 'Delivery', 'Driver Services', 'Courier', 'Vehicle Rental'] },
    'food-hospitality': { label: 'Food and Hospitality', subcategories: ['Personal Chef', 'Meal Prep', 'Baking', 'Catering', 'Food Delivery'] },
    'pet-services': { label: 'Pet Services', subcategories: ['Pet Grooming', 'Dog Walking', 'Pet Sitting', 'Pet Training', 'Veterinary Consultation'] },
    'health-medical': { label: 'Health and Medical', subcategories: ['Nursing', 'Physiotherapy', 'Home Care', 'Medical Consultation', 'Pharmacy'] },
    'fashion-design': { label: 'Fashion and Design', subcategories: ['Tailoring', 'Fashion Design', 'Interior Design', 'Alterations', 'Custom Clothing', 'Styling'] },
    'security-safety': { label: 'Security and Safety', subcategories: ['Security Guard', 'CCTV Installation', 'Alarm Systems', 'Home Security'] },
    'other-services': { label: 'Other Services', subcategories: ['Custom Service', 'Specialized Service', 'Other'] }
  };

  const availabilityOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mainCategory') setFormData(prev => ({ ...prev, mainCategory: value, subcategory: '' }));
    else setFormData({ ...formData, [name]: value });
  };

  const handleAvailabilityToggle = (day) => {
    setFormData({ ...formData, availability: formData.availability.includes(day) ? formData.availability.filter(d => d !== day) : [...formData.availability, day] });
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill) => setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (selectedImages.length + files.length > 5) {
      setError('You can only add up to 5 photos');
      setTimeout(() => setError(''), 3000);
      return;
    }
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setSelectedImages(prev => [...prev, ...newImages]);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess(false);
    try {
      const token = localStorage.getItem('token');
      if (!token) { window.location.href = '/auth'; return; }

      // Step 1: Create the service
      const res = await fetch(`${API_BASE}/api/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...formData, price: parseFloat(formData.price), responseTime: `${formData.responseTime} hours` }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Failed to post service.'); setLoading(false); return; }

      const serviceId = data.service?._id || data._id;

      // Step 2: Upload photos if any were selected
      if (selectedImages.length > 0 && serviceId) {
        const uploadData = new FormData();
        selectedImages.forEach(img => uploadData.append('images', img.file));
        await fetch(`${API_BASE}/api/upload/service/${serviceId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: uploadData,
        });
      }

      setSuccess(true);
      setTimeout(() => { window.location.href = '/dashboard'; }, 2000);
    } catch (err) {
      setError('Failed to connect to server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 py-8 px-4">
      <Navigation />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-green-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-green-400/20 rounded-full blur-3xl"></div>
      </div>
      <div className="relative max-w-3xl mx-auto pt-20">
        <button onClick={() => window.location.href = '/'} className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors">
          <ArrowLeft className="w-5 h-5" /><span className="font-medium">Back to Home</span>
        </button>
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
          <div className="p-8">
            <div className="text-center mb-8">
              <button onClick={() => window.location.href = '/'} className="inline-block hover:opacity-80 transition-opacity mb-4">
                <img src="/logos/logo-color.png" alt="HIVE" className="h-12 w-auto mx-auto dark:hidden" />
                <img src="/logos/logo-white.png" alt="HIVE" className="h-12 w-auto mx-auto hidden dark:block" />
              </button>
              <h1 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-2">Post Your Service</h1>
              <p className="text-gray-600 dark:text-gray-400">List your services and connect with customers looking for help</p>
            </div>

            {error && <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3"><AlertCircle className="w-5 h-5 text-red-600" /><p className="text-sm text-red-700 dark:text-red-300">{error}</p></div>}
            {success && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3"><CheckCircle className="w-5 h-5 text-green-600" /><p className="text-sm text-green-700 dark:text-green-300">Service posted successfully! Redirecting to dashboard...</p></div>}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Main Category *</label>
                  <select name="mainCategory" value={formData.mainCategory} onChange={handleInputChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100">
                    <option value="">Select category</option>
                    {Object.entries(serviceCategories).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Subcategory *</label>
                  <select name="subcategory" value={formData.subcategory} onChange={handleInputChange} required disabled={!formData.mainCategory} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 disabled:opacity-50">
                    <option value="">Select subcategory</option>
                    {formData.mainCategory && serviceCategories[formData.mainCategory].subcategories.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Service Title *</label>
                <input type="text" name="title" value={formData.title} onChange={handleInputChange} required placeholder="e.g., Professional Instagram Content Creator" className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Service Description *</label>
                <div className="relative">
                  <FileText className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                  <textarea name="description" value={formData.description} onChange={handleInputChange} required rows="5" placeholder="Describe your service, expertise and what makes you stand out..." className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Service Area *</label>
                <div className="relative">
                  <MapPin className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                  <input type="text" name="location" value={formData.location} onChange={handleInputChange} required placeholder="e.g., Colombo, Kandy, All Sri Lanka, Remote" className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Pricing Type *</label>
                  <select name="priceType" value={formData.priceType} onChange={handleInputChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100">
                    <option value="hourly">Per Hour</option>
                    <option value="daily">Per Day</option>
                    <option value="project">Per Project</option>
                    <option value="fixed">Fixed Price</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Price (LKR) *</label>
                  <div className="relative">
                    <DollarSign className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                    <input type="number" name="price" value={formData.price} onChange={handleInputChange} required min="0" placeholder="Enter your rate" className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Years of Experience *</label>
                <select name="experience" value={formData.experience} onChange={handleInputChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100">
                  <option value="">Select experience level</option>
                  <option value="0-1 years">Less than 1 year</option>
                  <option value="1-2 years">1-2 years</option>
                  <option value="2-5 years">2-5 years</option>
                  <option value="5-10 years">5-10 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="+94 77 123 4567" className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="your@email.com" className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Response Time *</label>
                <select name="responseTime" value={formData.responseTime} onChange={handleInputChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100">
                  <option value="1">Within 1 hour</option>
                  <option value="6">Within 6 hours</option>
                  <option value="24">Within 24 hours</option>
                  <option value="48">Within 48 hours</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Skills and Specializations</label>
                <div className="flex gap-2 mb-3">
                  <input type="text" value={skillInput} onChange={e => setSkillInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())} placeholder="e.g., Adobe Premiere, Canon Camera" className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                  <button type="button" onClick={handleAddSkill} className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all font-semibold">Add</button>
                </div>
                {formData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill, i) => (
                      <span key={i} className="inline-flex items-center px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-lg text-sm font-medium">
                        {skill}<button type="button" onClick={() => handleRemoveSkill(skill)} className="ml-2"><X className="w-4 h-4" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Availability *</label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {availabilityOptions.map(day => (
                    <button key={day} type="button" onClick={() => handleAvailabilityToggle(day)}
                      className={`py-2.5 rounded-xl font-medium text-sm transition-all ${formData.availability.includes(day) ? 'bg-green-500 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Select the days you are available to work</p>
              </div>

              {/* Portfolio Photos */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Camera className="w-4 h-4 text-green-500" /> Portfolio Photos <span className="text-gray-400 font-normal">(optional, up to 5)</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Add photos of your work to attract more customers.</p>

                {selectedImages.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                    {selectedImages.map((img, i) => (
                      <div key={i} className="relative group rounded-xl overflow-hidden aspect-square">
                        <img src={img.preview} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => handleRemoveImage(i)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {selectedImages.length < 5 && (
                  <>
                    <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" />
                    <button type="button" onClick={() => imageInputRef.current.click()}
                      className="w-full py-3 border-2 border-dashed border-green-300 dark:border-green-700 rounded-2xl text-green-600 dark:text-green-400 font-semibold hover:bg-green-50 dark:hover:bg-green-900/20 transition-all flex items-center justify-center gap-2">
                      <Camera className="w-4 h-4" /> Add Photos ({selectedImages.length}/5)
                    </button>
                  </>
                )}
              </div>

              <div className="pt-4">
                <button type="submit" disabled={loading} className={`w-full py-4 bg-gradient-to-r from-green-400 to-green-500 text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-green-400/50 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {loading ? <><Loader className="w-5 h-5 animate-spin" /> {selectedImages.length > 0 ? 'Posting and uploading photos...' : 'Posting Service...'}</> : 'List Your Service'}
                </button>
                <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">Your service will be visible to all customers in your service area</p>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[{ title: 'No Listing Fee', desc: 'List your services for free' }, { title: 'Get Job Requests', desc: 'Customers contact you directly' }, { title: 'Build Your Reputation', desc: 'Earn reviews and ratings' }].map((info, index) => (
            <div key={index} className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-2xl p-4 border border-gray-200/50 dark:border-gray-700/50 text-center hover:shadow-xl hover:scale-[1.03] transition-all duration-300">
              <h3 className="font-bold text-gray-900 dark:text-white mb-1">{info.title}</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">{info.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}